import React, { useContext, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link as RouterLink, useNavigate } from 'react-router-dom'

import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import Fab from '@mui/material/Fab'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

import AddIcon from '@mui/icons-material/Add'
import ModeEditIcon from '@mui/icons-material/ModeEdit'

import { ThemeContext, UserContext } from 'components/common/App'

import rpc, { buildRequest, signoutAndRedirectToIndex } from 'lib/rpc'

/**
 * Themed pack list (#278). Each pack groups mascots into a "3 of 12" collection
 * in the student catalog. Create/edit opens the membership editor.
 */
export default function PacksPage() {
  const theme = useContext(ThemeContext)
  // Creating/editing packs is superuser-only (#290).
  const currentUser = useContext(UserContext)
  const isSuperuser = currentUser && currentUser.role === "superuser"
  const navigate = useNavigate()
  const [packs, setPacks] = useState(null)

  useEffect(() => {
    theme.changeTheme("light-bg")
    window.scrollTo(0, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    rpc.post(
      "",
      buildRequest("admin.mascots.packs.list", { page: 1, per_page: 200, search: "" })
    ).then((response) => {
      const result = response.data.result
      if (result.message) {
        if (result.code === 5) signoutAndRedirectToIndex()
        console.error(result)
        return
      }
      setPacks(result.data)
    })
  }, [])

  if (packs === null) {
    return (
      <Container fixed sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    )
  }

  return (
    <>
      <Helmet>
        <title>Mascot Packs</title>
      </Helmet>

      {isSuperuser && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={() => navigate("/mascots/packs/create")}
        >
          <AddIcon />
        </Fab>
      )}

      <Container fixed>
        <Typography variant="h3" gutterBottom>
          Mascot Packs
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography color="text.secondary">
            Themed collections shown as "3 of 12" groups in the student catalog.
          </Typography>
        </Box>
        <Paper>
          <TableContainer>
            <Table size="small" aria-label="packs table">
              <TableHead>
                <TableRow>
                  <TableCell>Order</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell align="right">Members</TableCell>
                  <TableCell align="center">Active</TableCell>
                  <TableCell align="center">Edit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {packs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No packs yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  packs.map((pack) => (
                    <TableRow key={pack.id} hover>
                      <TableCell>{pack.display_order}</TableCell>
                      <TableCell>
                        <Link component={RouterLink} to={`/mascots/packs/details/${pack.id}`}>
                          {pack.name}
                        </Link>
                      </TableCell>
                      <TableCell>{pack.slug}</TableCell>
                      <TableCell align="right">{pack.member_count}</TableCell>
                      <TableCell align="center">
                        {pack.is_active ? (
                          <Chip size="small" color="success" label="Active" />
                        ) : (
                          <Chip size="small" label="Retired" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Link component={RouterLink} to={`/mascots/packs/details/${pack.id}`}>
                          <ModeEditIcon fontSize="small" />
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>
    </>
  )
}
